import { useState } from 'react';
import { Card, CardContent as CardBody } from '@shadcn/components/ui/card';
import { Button } from '@shadcn/components/ui/button';

const IntegrationChoiceStep = ({
  selectedIntegration,
  setSelectedIntegration,
  onNext,
  onBack,
  shopifyLinks,
  setShopifyLinks,
  amazonLinks,
  setAmazonLinks
}) => {
  const handleIntegrationSelect = (integration) => {
    
    if (selectedIntegration && selectedIntegration !== integration) {
      if (selectedIntegration === 'shopify') {
        setShopifyLinks([]);
      } else if (selectedIntegration === 'amazon') {
        setAmazonLinks([]);
      }
    }
    
    setSelectedIntegration(integration);
  };

  return (
    <div className="p-2 md:p-4">
      <div className="flex items-center gap-2 mb-4 md:mb-6">
        <i className="bx bx-link text-blue-500 text-2xl"></i>
        <h4 className="text-lg md:text-xl font-medium">Choose Integration Platform</h4>
      </div>

      <div className="space-y-4 md:space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <i className="bx bx-info-circle text-blue-500 text-xl mt-0.5"></i>
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Platform Selection:</p>
              <p className="text-blue-600">Select which e-commerce platform you want to integrate with this location. You can only choose one platform per location, or select "No Integration" to skip this step.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <Card
            className={`cursor-pointer transition-all duration-300 hover:shadow-lg transform hover:scale-[1.02] ${
              selectedIntegration === 'shopify'
                ? 'border-green-500 shadow-md bg-green-50 ring-2 ring-green-200'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
            }`}
            onClick={() => handleIntegrationSelect('shopify')}
          >
            <CardBody className="p-6 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className={`p-3 rounded-full ${
                  selectedIntegration === 'shopify' ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  <i className={`bx bxl-shopify text-4xl ${
                    selectedIntegration === 'shopify' ? 'text-green-600' : 'text-gray-600'
                  }`}></i>
                </div>
                <h5 className="text-lg font-semibold text-gray-800">Shopify</h5>
                <p className="text-sm text-gray-600">
                  Connect your Shopify store to sync products and inventory
                </p>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <i className="bx bx-check-circle"></i>
                  <span>Push/Pull/None sync options</span>
                </div>
                {selectedIntegration === 'shopify' && (
                  <div className="mt-2">
                    <i className="bx bx-check-circle text-green-500 text-xl"></i>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          <Card
            className={`cursor-pointer transition-all duration-300 hover:shadow-lg transform hover:scale-[1.02] ${
              selectedIntegration === 'amazon'
                ? 'border-orange-500 shadow-md bg-orange-50 ring-2 ring-orange-200'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
            }`}
            onClick={() => handleIntegrationSelect('amazon')}
          >
            <CardBody className="p-6 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className={`p-3 rounded-full ${
                  selectedIntegration === 'amazon' ? 'bg-orange-100' : 'bg-gray-100'
                }`}>
                  <i className={`bx bxl-amazon text-4xl ${
                    selectedIntegration === 'amazon' ? 'text-orange-500' : 'text-gray-600'
                  }`}></i>
                </div>
                <h5 className="text-lg font-semibold text-gray-800">Amazon</h5>
                <p className="text-sm text-gray-600">
                  Connect your Amazon Seller Central account to manage listings
                </p>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <i className="bx bx-check-circle"></i>
                  <span>Default sync settings</span>
                </div>
                {selectedIntegration === 'amazon' && (
                  <div className="mt-2">
                    <i className="bx bx-check-circle text-orange-500 text-xl"></i>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          <Card
            className={`cursor-pointer transition-all duration-300 hover:shadow-lg transform hover:scale-[1.02] ${
              selectedIntegration === 'none'
                ? 'border-gray-500 shadow-md bg-gray-50 ring-2 ring-gray-200'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
            }`}
            onClick={() => handleIntegrationSelect('none')}
          >
            <CardBody className="p-6 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className={`p-3 rounded-full ${
                  selectedIntegration === 'none' ? 'bg-gray-200' : 'bg-gray-100'
                }`}>
                  <i className={`bx bx-x-circle text-4xl ${
                    selectedIntegration === 'none' ? 'text-gray-600' : 'text-gray-500'
                  }`}></i>
                </div>
                <h5 className="text-lg font-semibold text-gray-800">No Integration</h5>
                <p className="text-sm text-gray-600">
                  Skip e-commerce platform integration for this location
                </p>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <i className="bx bx-info-circle"></i>
                  <span>Can be added later</span>
                </div>
                {selectedIntegration === 'none' && (
                  <div className="mt-2">
                    <i className="bx bx-check-circle text-gray-500 text-xl"></i>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>

        {selectedIntegration && selectedIntegration !== 'none' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <i className="bx bx-error text-amber-500 text-xl mt-0.5"></i>
              <div className="text-sm text-amber-700">
                <p className="font-medium mb-1">Important:</p>
                <ul className="list-disc list-inside space-y-1 text-amber-600">
                  <li>You can only integrate with one platform per location</li>
                  {selectedIntegration === 'shopify' && (
                    <>
                      <li>Shopify allows sync type configuration (None, Push, Pull)</li>
                      {amazonLinks.length > 0 && (
                        <li className="text-red-600">Previous Amazon configuration will be cleared</li>
                      )}
                    </>
                  )}
                  {selectedIntegration === 'amazon' && (
                    <>
                      <li>Amazon integration will use default sync settings</li>
                      {shopifyLinks.length > 0 && (
                        <li className="text-red-600">Previous Shopify configuration will be cleared</li>
                      )}
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {selectedIntegration === 'none' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <i className="bx bx-check-circle text-green-500 text-xl mt-0.5"></i>
              <div className="text-sm text-green-700">
                <p className="font-medium mb-1">No Integration Selected:</p>
                <ul className="list-disc list-inside space-y-1 text-green-600">
                  <li>You can add integration later from the location settings</li>
                  <li>All existing configurations will be cleared</li>
                  <li>This location will function without e-commerce platform integration</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default IntegrationChoiceStep;